// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { forwardRef } from 'react';
import { toast } from '../lib/buses';
import { useConfirm } from '../lib/hooks';

/** Keyboard focus ring of the controls (shown on keyboard focus only). */
export const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/60 focus-visible:ring-offset-1';

// Button sizes to ITS LABEL. In flex toolbars (blurb + actions, page headers,
// section card actions) a naked `inline-flex` still has flex-shrink:1 and
// white-space:normal — multi-word labels ("New fee rule", "Open event") wrap or
// get crushed to a width that isn't the content. Always nowrap + shrink-0 so
// the box is content-width; pass className="w-full" when a full-bleed button
// is intentional (modals, empty states).
const BUTTON_VARIANTS = {
  primary: 'bg-[#1F2937] text-white border border-[#1F2937] hover:bg-[#111827]',
  secondary: 'bg-white text-[#1F2937] border border-[#E6E4E1] hover:bg-[#FAF9F6]',
  ghost: 'bg-transparent text-[#4B5563] hover:bg-[#F2F1ED] border border-transparent',
  danger: 'bg-white text-[#9B1F1F] border border-[#F1D2D2] hover:bg-[#FBE9E9]',
  link: 'bg-transparent text-[#6B7280] border border-transparent hover:text-[#1F2937] hover:underline underline-offset-2 px-0',
  // Same shape as `link`, red. A separate VARIANT rather than a red className on `link`, because a
  // caller's `text-*` and the variant's `text-*` have equal specificity — which of them wins is
  // decided by stylesheet order, not by the call site's intent. Anything that must override a
  // variant belongs in the variant table.
  'link-danger': 'bg-transparent text-[#9B1F1F] border border-transparent hover:text-[#7F1D1D] hover:underline underline-offset-2 px-0',
  // A pressed toolbar button (the panel it opens is showing).
  active: 'bg-[#F2F1ED] text-[#1F2937] border border-[#E6E4E1]',
};
const BUTTON_SIZES = {
  xs: 'h-[22px] px-2 text-[11.5px] gap-1',
  sm: 'h-7 px-2.5 text-[12px] gap-1.5',
  md: 'h-8 px-3 text-[12.5px] gap-1.5',
  lg: 'h-9 px-4 text-[13px] gap-2',
  // 44px, for touch layouts.
  touch: 'h-11 px-3 text-[13px] gap-2',
};

/**
 * Button.
 *   variant  primary | secondary | ghost | danger | link | link-danger | active (a pressed toolbar button)
 *   size     xs (22px) | sm (28px) | md (32px) | lg (36px) | touch (44px)
 *   icon, iconRight  lucide icons before and after the label
 * Forwards its ref (popover triggers need it). No default `type`: inside a form it submits, as before.
 */
export const Button = forwardRef(({ variant = 'secondary', size = 'md', icon: Icon, iconRight: IconRight, children, onClick, className = '', disabled = false, ...props }, ref) => {
  const variants = BUTTON_VARIANTS;
  const sizes = BUTTON_SIZES;
  // A LINK IS TEXT, NOT A BOX. The size tokens carry a fixed height and horizontal padding, which
  // is right for every other variant and wrong for this one: `link` sets `px-0`, and two padding
  // utilities of equal specificity leave the winner to stylesheet order rather than intent — so the
  // variant only *looked* like it worked. Dropping h-* and px-* here means a link sits on the text
  // baseline of whatever row it is in and keeps only the size's font and gap.
  const sizeClass = variant.startsWith('link')
    ? (sizes[size] || sizes.md).replace(/\bh-(?:\d+|\[[^\]]+\])/, '').replace(/\bpx-[\d.]+\b/, '').trim()
    : sizes[size];
  const icon = `${size === 'sm' || size === 'xs' ? 'w-3.5 h-3.5' : 'w-4 h-4'} shrink-0`;
  return (
    <button
      ref={ref}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={`inline-flex items-center justify-center whitespace-nowrap shrink-0 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING} ${variants[variant] || variants.secondary} ${sizeClass || sizes.md} ${className}`}
      {...props}
    >
      {Icon && <Icon className={icon} strokeWidth={2} aria-hidden="true" />}
      {children}
      {IconRight && <IconRight className={`${icon} opacity-80`} strokeWidth={2} aria-hidden="true" />}
    </button>
  );
});
Button.displayName = 'Button';

// Blurb-left / actions-right row used on almost every panel. The long blurb
// must min-w-0 flex-1 so it yields space; the action cluster must shrink-0 so
// buttons keep content width (pairs with Button's own shrink-0/nowrap).
export const ActionRow = ({ blurb, children, className = '' }) => (
  <div className={`flex items-start sm:items-center justify-between gap-3 ${className}`}>
    {blurb != null && blurb !== false && (
      <div className="min-w-0 flex-1 text-[12px] text-[#6B7280] leading-relaxed">{blurb}</div>
    )}
    {children != null && (
      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">{children}</div>
    )}
  </div>
);


// Unsaved-changes bar — sticky at top of settings pages
